"use client";

import { useState } from "react";

import Image from "next/image";

import type { PropertyImage } from "@unreserved/types/property-details/ui-types";

import { Dialog, DialogContent, DialogTitle } from "@/app/_components/shared/dialog";
import { VideoPlayer } from "@/app/_components/shared/video-player";
import { cn } from "@/lib/utils";

interface ImageSlideshowProps {
  images: PropertyImage[];
  initialIndex?: number;
  trigger?: React.ReactNode;
  className?: string;
}

export function ImageSlideshow({
  images,
  initialIndex = 0,
  trigger,
  className,
}: ImageSlideshowProps) {
  const [open, setOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  const currentImage = images[currentIndex];
  const totalImages = images.length;

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? totalImages - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === totalImages - 1 ? 0 : prev + 1));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowLeft") goToPrevious();
    if (e.key === "ArrowRight") goToNext();
    if (e.key === "Escape") setOpen(false);
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) {
      setCurrentIndex(initialIndex);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {trigger && (
        <div onClick={() => setOpen(true)} className={cn("cursor-pointer", className)}>
          {trigger}
        </div>
      )}
      {totalImages > 1 ? (
        <DialogContent
          className="h-[90vh] w-full max-w-7xl border-none bg-black/95 p-0"
          onKeyDown={handleKeyDown}>
          {/* Visually hidden title for accessibility */}
          <DialogTitle className="sr-only">Image Gallery - {currentImage.alt}</DialogTitle>

          <div className="relative flex h-full w-full items-center justify-center">
            {/* Close Button */}
            <button
              onClick={() => setOpen(false)}
              className="absolute top-4 right-4 z-50 rounded-full bg-gray-800/80 p-2 text-white transition-colors hover:bg-gray-700/90"
              aria-label="Close slideshow">
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* Previous Button */}
            {totalImages > 1 && (
              <button
                onClick={goToPrevious}
                className="absolute left-4 z-50 rounded-full bg-gray-800/80 p-3 text-white transition-colors hover:bg-gray-700/90"
                aria-label="Previous image">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
            )}

            {/* Image or Video */}
            <div className="relative flex h-full w-full items-center justify-center">
              {currentImage.type === "video" && currentImage.videoUrl ? (
                <div className="w-full max-w-5xl px-4">
                  <VideoPlayer
                    src={currentImage.videoUrl}
                    poster={currentImage.posterUrl || ""}
                    title={currentImage.alt}
                    className="w-full"
                  />
                </div>
              ) : (
                <Image
                  src={currentImage.url}
                  alt={currentImage.alt}
                  fill
                  className="object-contain"
                  sizes="(max-width: 1280px) 100vw, 1280px"
                  priority
                />
              )}
            </div>

            {/* Next Button */}
            {totalImages > 1 && (
              <button
                onClick={goToNext}
                className="absolute right-4 z-50 rounded-full bg-gray-800/80 p-3 text-white transition-colors hover:bg-gray-700/90"
                aria-label="Next image">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            )}

            {/* Image Counter */}
            {totalImages > 1 && (
              <div className="absolute bottom-4 left-1/2 z-50 -translate-x-1/2 rounded-full bg-black/50 px-4 py-2 text-sm font-medium text-white">
                {currentIndex + 1} / {totalImages}
              </div>
            )}

            {/* Thumbnail Navigation (Optional - for many images) */}
            {totalImages > 5 && (
              <div className="absolute bottom-16 left-1/2 z-50 flex max-w-[80%] -translate-x-1/2 gap-2 overflow-x-auto pt-2 pb-2">
                {images.map((image, index) => (
                  <button
                    key={image.id}
                    onClick={() => setCurrentIndex(index)}
                    className={cn(
                      "relative h-16 w-16 flex-shrink-0 overflow-hidden rounded border-2 transition-all",
                      index === currentIndex
                        ? "ml-1 scale-110 border-white shadow-lg"
                        : "border-transparent opacity-60 hover:opacity-100",
                    )}>
                    <Image
                      src={image.url}
                      alt={image.alt}
                      fill
                      className="object-cover"
                      sizes="64px"
                    />
                    {image.type === "video" && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                        <svg className="h-4 w-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      ) : (
        <></>
      )}
    </Dialog>
  );
}
